/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useEffect, useState } from "react";
import { ListItem, ListItemText, ListItemSecondaryAction, IconButton, Grid } from "@material-ui/core";
import { Edit, Delete } from "@material-ui/icons";
import { format, isValid } from "date-fns";
import { KeyboardDatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import { ActionButtons } from "../../../../components/Common";

/**
 * This component is used for displaying nad potentially editing a holiday.
 * @param {*} overwrite The overwrite to be displayed.
 * @param {*} editOverwrite The callback method to be called when the overwrite is edited.
 * @param {*} deleteOverwrite The callback method to be called when the overwrite is deleted.
 * @param {*} holidaysToDisplay The list of all the overwrites so that when editing we dont add 2 overwrites with the same times.
 */
export const OverwritesCard = ({
	overwrite,
	editOverwrite,
	setIsLoading,
	deleteOverwrite,
	overwritesToDisplay,
	setItemModified,
}) => {
	const [startDate, setStartDate] = useState(new Date(overwrite.startDate));
	const [endDate, setEndDate] = useState(new Date(overwrite.endDate));
	const [formEdited, setFormEdited] = useState(false);
	const [editMode, setEditMode] = useState(false);

	const errorMessage = startDate >= endDate ? "End date and time must come after start date and time" : undefined;
	const formatString = "MM/dd/yyyy";

	useEffect(() => {
		const isModified =
			overwrite.startDate !== startDate.toLocaleString() || overwrite.endDate !== endDate.toLocaleString();
		setFormEdited(isModified);
		setItemModified(isModified, overwrite.overwriteId);
	}, [overwrite, startDate, endDate, setItemModified]);

	const saveButtonDisabled =
		(overwritesToDisplay &&
			overwritesToDisplay.filter((overwrite) => overwrite.startDate === startDate && overwrite.endDate === endDate)
				.length !== 0) ||
		startDate > endDate;
	return (
		<ListItem>
			{editMode ? (
				<>
					<Grid container justifyContent="space-between" spacing={2} className="mt-2">
						<Grid item xs={12}>
							<ActionButtons
								showCancel
								showRemove
								showSave={formEdited}
								onCancel={() => {
									setEndDate(new Date(overwrite.endDate));
									setStartDate(new Date(overwrite.startDate));
									setEditMode(false);
									setItemModified(false, overwrite.overwriteId);
								}}
								onRemove={deleteOverwrite}
								onSave={async () => {
									setIsLoading(true);
									await editOverwrite(startDate, endDate);
									setEditMode(false);
									setItemModified(false, overwrite.overwriteId);
									setIsLoading(false);
								}}
								disableSave={saveButtonDisabled}
								className="float-right"
							/>
						</Grid>
						<Grid item xs={12} md={6} className="mt-2">
							<KeyboardDatePicker
								disableToolbar
								variant="inline"
								format={formatString}
								margin="normal"
								id="start-date-picker-inline"
								label="Start Date"
								value={startDate}
								minDate={new Date()}
								onChange={(date) => {
									let newStartTime = date;
									if (isValid(newStartTime)) {
										let newStartDate = newStartTime;
										newStartDate.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
										setStartDate(new Date(newStartDate));
									}
								}}
								KeyboardButtonProps={{
									"aria-label": "change date",
								}}
								className="w-100 m-0"
							/>
						</Grid>
						<Grid item xs={12} md={6} className="mt-2">
							<KeyboardTimePicker
								disableToolbar
								className="w-100 m-0"
								label="Start Time"
								variant="inline"
								ampm
								value={startDate}
								onChange={(date) => {
									let newStartTime = date;
									if (isValid(newStartTime)) {
										let newStartDate = startDate;
										newStartDate.setHours(newStartTime.getHours(), newStartTime.getMinutes(), 0, 0);
										setStartDate(new Date(newStartDate));
									}
								}}
							/>
						</Grid>
						<Grid item xs={12} md={6} className="mt-2">
							<KeyboardDatePicker
								disableToolbar
								variant="inline"
								format={formatString}
								margin="normal"
								id="end-date-picker-inline"
								label="End Date"
								value={endDate}
								error={startDate > endDate}
								minDate={startDate}
								minDateMessage={""}
								onChange={(date) => {
									let newEndDate = date;
									if (isValid(newEndDate)) {
										newEndDate.setHours(endDate.getHours(), endDate.getMinutes(), 0, 0);
										setEndDate(new Date(newEndDate));
									}
								}}
								KeyboardButtonProps={{
									"aria-label": "change date",
								}}
								className="w-100 m-0"
							/>
						</Grid>
						<Grid item xs={12} md={6} className="mt-2">
							<KeyboardTimePicker
								disableToolbar
								className="w-100 m-0"
								label="End Time"
								variant="inline"
								ampm
								value={endDate}
								error={startDate > endDate}
								minDate={startDate}
								onChange={(date) => {
									let newEndTime = date;
									if (isValid(newEndTime)) {
										let newEndDate = endDate;
										newEndDate.setHours(newEndTime.getHours(), newEndTime.getMinutes(), 0, 0);
										setEndDate(new Date(newEndDate));
									}
								}}
							/>
						</Grid>
						{errorMessage !== undefined && (
							<Grid item xs={12} className="mt-2">
								<div style={{ color: "#cc1f20", marginLeft: "10px" }}>{errorMessage}</div>
							</Grid>
						)}
					</Grid>
				</>
			) : (
				<>
					<ListItemText
						primary={format(new Date(overwrite.startDate), "MM/dd/yyyy hh:mm a")}
						secondary="Start Date Time"
					/>
					<ListItemText primary={format(new Date(overwrite.endDate), "MM/dd/yyyy hh:mm a")} secondary="End Date Time" />
					<ListItemSecondaryAction>
						<IconButton edge="end" aria-label="delete" onClick={deleteOverwrite}>
							<Delete />
						</IconButton>
						<IconButton edge="end" aria-label="edit" onClick={() => setEditMode(true)}>
							<Edit />
						</IconButton>
					</ListItemSecondaryAction>
				</>
			)}
		</ListItem>
	);
};
